import { DocumentData } from "@angular/fire/firestore"
import { StudyOrderTemplate as BackendStudyOrderTemplate, StudyOrder } from '@longtail-education/superprep-data-model-frontend'
import { groupBy, localeCompare, toUniqueNonNullableArray } from "@nx-superprep/utils"

/**
 * Represents a study order template.
 */
export class StudyOrderTemplate extends BackendStudyOrderTemplate {
  /**
   * Creates an instance of StudyOrderTemplate.
   * @param moduleId The module ID.
   * @param studyOrders The study orders.
   */
  constructor(moduleId: string, studyOrders: StudyOrder[]) {
    super(moduleId)
    this.studyOrders = studyOrders ?? []
  }

  /**
   * Static converter methods for Firestore conversion.
   */
  static override converter = {
    /**
     * Convert StudyOrderTemplate to Firestore data.
     * @param value The StudyOrderTemplate instance.
     * @returns The Firestore data.
     */
    toFirestore(value: StudyOrderTemplate) {
      return Object.assign({
        studyOrders: StudyOrderTemplate.cleanup(value.studyOrders),
      })
    },
    /**
     * Convert Firestore data to StudyOrderTemplate.
     */
    fromFirestore: BackendStudyOrderTemplate.converter.fromFirestore,
    /**
     * Convert Firestore document data to StudyOrderTemplate.
     * @param documentData The document data.
     * @returns The StudyOrderTemplate instance.
     */
    fromFirestoreDocumentData(documentData: DocumentData): StudyOrderTemplate {
      return new StudyOrderTemplate(
        documentData['id'] ?? documentData['moduleId'],
        documentData['studyOrders']
      )
    }
  }

  /**
   * Static method to cleanup study orders.
   * @param items The study orders to cleanup.
   * @returns The cleaned-up study orders.
   */
  static cleanup(items: StudyOrder[] = []) {
    return Object.entries(
      groupBy(items, 'fullPathElementId')
    )
    .map(([k, v]) => ({fullPathElementId: k, cardIds: toUniqueNonNullableArray(v.flatMap(s => s.cardIds))}))
    .sort((a,b) => localeCompare(a.fullPathElementId, b.fullPathElementId))
  }

  /**
   * Gets whether the study order template is valid.
   * @returns True if the study order template is valid, otherwise false.
   */
  get isValid() {
    return this.id && (this.studyOrders ?? []).length > 0
  }
}
